import { EncounterRegionNoteModel } from 'src/app/api/proxy/practice/models/encounter-region-note-model';
import { NotesModel } from 'src/app/api/proxy/practice/models/notes-model';
import { TreatmentModel } from 'src/app/api/proxy/practice/models/treatment-model';
import { TreatmentNoteModel } from 'src/app/api/proxy/practice/models/treatment-note-model';
import {
  buildRegionNoteModels,
  buildTreatmentNoteModel,
  partDataToPartFindingModels,
  treatmentNoteToTreatmentState,
  type SoapRegionPartState,
  type SoapTreatmentPartState,
} from 'src/app/components/soap/part-finding.mapper';
import {
  filterPartDataPerLevelMapForDuplicate,
  filterRegionNotesForDuplicate,
  filterRegionPartStateMapForDuplicate,
} from 'src/app/components/soap/part-finding.mapper';
import {
  parseSoapObjectivePayload,
  unwrapSoapObjectiveNotes,
  wrapSoapObjectivePayload,
} from 'src/app/components/soap/soap-objective.util';

export interface PendingSoapFlushPayload {
  patientId: string;
  notes: NotesModel;
  treatments: TreatmentModel[];
  regionNotes?: EncounterRegionNoteModel[] | null;
}

export function notesHaveSoapContent(note: NotesModel | null | undefined): boolean {
  if (!note) {
    return false;
  }

  return !!(
    note.subjectiveNotes ||
    unwrapSoapObjectiveNotes(note.objectiveNotes) ||
    note.assessmentNotes ||
    note.planNotes
  );
}

/** Encounter SOAP wins after save; queue snapshot fills in when the encounter has none. */
export function preferEncounterOrQueuedSoapNotes(
  encounterNotes: NotesModel | null | undefined,
  queuedNotes: NotesModel | null | undefined,
): NotesModel | null {
  if (notesHaveSoapContent(encounterNotes)) {
    return encounterNotes ?? null;
  }
  if (notesHaveSoapContent(queuedNotes)) {
    return queuedNotes ?? null;
  }
  return encounterNotes ?? queuedNotes ?? null;
}

export function soapNoteForCompleteEncounter(
  note: NotesModel | null | undefined,
): NotesModel | undefined {
  return notesHaveSoapContent(note) ? note ?? undefined : undefined;
}

/** Pending SOAP wins when both have content; encounter fills in when pending is empty. */
export function resolveSoapNotesForCompleteEncounter(
  encounterNotes: NotesModel | null | undefined,
  pendingNotes: NotesModel | null | undefined,
): NotesModel | undefined {
  if (notesHaveSoapContent(pendingNotes)) {
    const pending = pendingNotes!;
    const pendingId = pending.id ?? null;
    const encounterId = encounterNotes?.id ?? null;
    if (pendingId && encounterId && pendingId !== encounterId) {
      const { id, ...withoutId } = pending;
      return withoutId;
    }
    return pending;
  }
  if (notesHaveSoapContent(encounterNotes)) {
    return encounterNotes ?? undefined;
  }
  return encounterNotes ?? pendingNotes ?? undefined;
}

/** Copy SOAP text onto a new visit without the source note id or practitioner stamp. */
export function cloneSoapNotesForDuplicate(
  notes: NotesModel | null | undefined,
): NotesModel | null {
  if (!notes) {
    return null;
  }

  const subjectiveNotes = notes.subjectiveNotes ?? null;
  const objectiveNotes = notes.objectiveNotes ?? null;
  const assessmentNotes = notes.assessmentNotes ?? null;
  const planNotes = notes.planNotes ?? null;

  if (!subjectiveNotes && !objectiveNotes && !assessmentNotes && !planNotes) {
    return null;
  }

  return {
    subjectiveNotes,
    objectiveNotes,
    assessmentNotes,
    planNotes,
  };
}

/**
 * PART/SOAP Save (including the notes unsaved-changes dialog) always posts when
 * the encounter already exists. Edit still posts; it only skips CURRENT-draft staging.
 */
export function planSoapUserSave(options: {
  encounterId?: string | null;
  persistOnCurrentDraft: boolean;
}): { postToBackend: boolean; stageCurrentDraft: boolean } {
  return {
    postToBackend: !!options.encounterId,
    stageCurrentDraft: options.persistOnCurrentDraft,
  };
}

/** Copy region-scoped PART notes/observations onto a new visit without source ids. */
export function cloneRegionNotesForDuplicate(
  regionNotes: EncounterRegionNoteModel[] | null | undefined,
): EncounterRegionNoteModel[] | null {
  if (!regionNotes?.length) {
    return null;
  }

  const cloned = regionNotes
    .map((note) => {
      const partFindings = note.partFindings?.map((finding) => ({
        partType: finding.partType,
        observationIds: finding.observationIds ? [...finding.observationIds] : [],
      }));

      return {
        partFindingAnatomyRegion: note.partFindingAnatomyRegion,
        description: note.description ?? null,
        partFindings: partFindings?.length ? partFindings : null,
      };
    })
    .filter((note) => !!note.partFindingAnatomyRegion);

  return cloned.length ? cloned : null;
}

function clonePartDataMapForDuplicate(
  map: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!map || Object.keys(map).length === 0) {
    return null;
  }

  return JSON.parse(JSON.stringify(map)) as Record<string, unknown>;
}

/** Resolve region PART state from saved notes, pending draft, or legacy objective JSON. */
export function resolveRegionNotesForDuplicate(options: {
  regionNotes?: EncounterRegionNoteModel[] | null;
  pendingRegionPartDataByNoteRegion?: Record<string, unknown> | null;
  objectiveNotes?: string | null;
}): EncounterRegionNoteModel[] | null {
  const fromEncounter = cloneRegionNotesForDuplicate(options.regionNotes);
  if (fromEncounter?.length) {
    return fromEncounter;
  }

  const pending = options.pendingRegionPartDataByNoteRegion;
  if (pending && Object.keys(pending).length > 0) {
    const models = buildRegionNoteModels(pending as Record<string, SoapRegionPartState>);
    return models.length ? models : null;
  }

  const legacy = parseSoapObjectivePayload(options.objectiveNotes);
  if (legacy?.regionPartDataByNoteRegion && Object.keys(legacy.regionPartDataByNoteRegion).length > 0) {
    const models = buildRegionNoteModels(legacy.regionPartDataByNoteRegion);
    return models.length ? models : null;
  }

  return null;
}

/** Resolve per-level PART grid state from pending draft or legacy objective JSON. */
export function resolvePartDataPerLevelForDuplicate(options: {
  pendingPartDataPerLevelByLevelName?: Record<string, unknown> | null;
  objectiveNotes?: string | null;
}): Record<string, unknown> | null {
  const pending = clonePartDataMapForDuplicate(options.pendingPartDataPerLevelByLevelName);
  if (pending) {
    return pending;
  }

  const legacy = parseSoapObjectivePayload(options.objectiveNotes);
  if (
    legacy?.partDataPerLevelByLevelName &&
    Object.keys(legacy.partDataPerLevelByLevelName).length > 0
  ) {
    return clonePartDataMapForDuplicate(
      legacy.partDataPerLevelByLevelName as Record<string, unknown>,
    );
  }

  return null;
}

/** Strip foreign custom observation ids from structured objective JSON on duplicate. */
export function filterSoapObjectiveNotesForDuplicate(
  objectiveNotes: string | null | undefined,
  allowedObservationIds: ReadonlySet<string>,
): string | null {
  const payload = parseSoapObjectivePayload(objectiveNotes);
  if (!payload) {
    return objectiveNotes ?? null;
  }

  const partDataPerLevelByLevelName = filterPartDataPerLevelMapForDuplicate(
    payload.partDataPerLevelByLevelName as Record<string, unknown> | undefined,
    allowedObservationIds,
  );
  const regionPartDataByNoteRegion = filterRegionPartStateMapForDuplicate(
    payload.regionPartDataByNoteRegion as Record<string, unknown> | undefined,
    allowedObservationIds,
  );

  return wrapSoapObjectivePayload(
    payload.soapObjectiveNotes,
    partDataPerLevelByLevelName,
    regionPartDataByNoteRegion,
  );
}

/** Pending SOAP/PART drafts are per current encounter, not per patient. */
export function pendingNotesApplyToEncounter(
  viewingEncounterId: string | null | undefined,
  currentEncounterId: string | null | undefined,
): boolean {
  return (viewingEncounterId ?? null) === (currentEncounterId ?? null);
}

export function treatmentsHaveFindingsPayload(
  treatments: TreatmentModel[] | null | undefined,
  regionNotes?: EncounterRegionNoteModel[] | null,
): boolean {
  const hasTreatmentNotes = (treatments ?? []).some((treatment) => {
    if (treatment.treatmentNote) {
      const note = treatment.treatmentNote;
      if ((note.description ?? '').trim()) {
        return true;
      }
      if (note.partFindings?.length) {
        return true;
      }
    }
    return false;
  });

  return hasTreatmentNotes || !!(regionNotes?.length);
}

export function hasPendingSoapFlushPayload(options: {
  notes?: NotesModel | null;
  treatments?: TreatmentModel[] | null;
  regionNotes?: EncounterRegionNoteModel[] | null;
}): boolean {
  return (
    notesHaveSoapContent(options.notes)
    || treatmentsHaveFindingsPayload(options.treatments, options.regionNotes)
  );
}

export function applyServerTreatmentIds(
  localTreatments: TreatmentModel[],
  serverTreatments: TreatmentModel[],
): TreatmentModel[] {
  const serverByDisplayIndex = new Map<number, TreatmentModel[]>();
  for (const treatment of serverTreatments) {
    const displayIndex = treatment.displayIndex ?? 0;
    const group = serverByDisplayIndex.get(displayIndex) ?? [];
    group.push(treatment);
    serverByDisplayIndex.set(displayIndex, group);
  }

  const usedSlot = new Map<number, number>();
  return localTreatments.map((treatment) => {
    const displayIndex = treatment.displayIndex ?? 0;
    const slot = usedSlot.get(displayIndex) ?? 0;
    usedSlot.set(displayIndex, slot + 1);
    const match = serverByDisplayIndex.get(displayIndex)?.[slot];
    return match?.id ? { ...treatment, id: match.id } : treatment;
  });
}

export function buildTreatmentNoteModelsFromTreatments(
  treatments: TreatmentModel[],
): TreatmentNoteModel[] | undefined {
  const models: TreatmentNoteModel[] = [];

  for (const treatment of treatments) {
    if (!treatment.id) {
      continue;
    }

    const state = treatmentNoteToTreatmentState(treatment.treatmentNote);
    const hasNote = !!(state.treatmentNote || '').trim();
    const hasFindings = partDataToPartFindingModels(state.partData).length > 0;

    if (!hasNote && !hasFindings) {
      continue;
    }

    models.push(buildTreatmentNoteModel(treatment.id, state));
  }

  return models.length ? models : undefined;
}

export function buildRegionNoteModelsFromStateMap(
  regionStateMap: Record<string, SoapRegionPartState>,
): EncounterRegionNoteModel[] | undefined {
  const models = buildRegionNoteModels(regionStateMap);
  return models.length ? models : undefined;
}

export function resolveRegionNotesForPendingFlush(options: {
  draftRegionNotes?: EncounterRegionNoteModel[] | null;
  pendingRegionPartDataByNoteRegion?: Record<string, unknown> | null;
}): EncounterRegionNoteModel[] | null {
  if (options.draftRegionNotes?.length) {
    return options.draftRegionNotes;
  }

  const pending = options.pendingRegionPartDataByNoteRegion;
  if (pending && Object.keys(pending).length > 0) {
    return buildRegionNoteModelsFromStateMap(pending as Record<string, SoapRegionPartState>) ?? null;
  }

  return null;
}

export function buildTreatmentNoteModelsFromStateMap(
  stateMap: Record<string, SoapTreatmentPartState>,
  treatmentIdForKey: (key: string) => string | null | undefined,
): TreatmentNoteModel[] | undefined {
  const entries: Array<{ treatmentId: string; state: SoapTreatmentPartState }> = [];

  for (const [key, state] of Object.entries(stateMap)) {
    const treatmentId = treatmentIdForKey(key);
    if (!treatmentId) {
      continue;
    }
    entries.push({ treatmentId, state });
  }

  const models = entries
    .filter(({ state }) => {
      const hasNote = !!(state.treatmentNote || '').trim();
      const hasFindings = partDataToPartFindingModels(state.partData).length > 0;
      return hasNote || hasFindings;
    })
    .map(({ treatmentId, state }) => buildTreatmentNoteModel(treatmentId, state));

  return models.length ? models : undefined;
}
